import { exportXlsModal } from 'motion-components';
import { PIVOT_QUERY, PivotQueryResult } from './graphql';
import { buildWhereFromFilterValues } from './filters';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

// Keep: one column per dynamic colKey, with human label as title
const buildColumns = (t: (k: string) => string, cols: Array<{ key: string; title: string }>) => [
  { fieldName: 'rowLabel', fieldTitle: t('Γραμμή'), fieldType: 'TEXT' },
  ...cols.map((c) => ({
    fieldName: c.key,
    fieldTitle: c.title,
    fieldType: 'NUMBER',
  })),
  { fieldName: 'rowTotal', fieldTitle: t('Σύνολο Γραμμής'), fieldType: 'NUMBER' }, // added
];

// Flatten -> Wide using colKey as stable property name
const pivotToWide = (flat: Array<{ rowLabel: string; colKey: string; value: number }>) => {
  const colKeys: string[] = [];
  const seen = new Set<string>();
  const grouped = new Map<string, any>();

  for (const r of flat) {
    if (!grouped.has(r.rowLabel)) grouped.set(r.rowLabel, { rowLabel: r.rowLabel, rowTotal: 0 });
    const rowObj = grouped.get(r.rowLabel);
    rowObj[r.colKey] = r.value;
    rowObj.rowTotal += r.value; // accumulate per row
    if (!seen.has(r.colKey)) {
      seen.add(r.colKey);
      colKeys.push(r.colKey);
    }
  }

  return { data: Array.from(grouped.values()), colKeys };
};

export async function handleExport(
  client: ApolloClient<NormalizedCacheObject>, // instead of `any`
  queryState: any,
  t: (k: string) => string,
): Promise<void> {
  // Build variables exactly like before
  const variables = {
    table: queryState?.tableId,
    rowDims: queryState?.rowDims,
    colDims: queryState?.colDims,
    measureId: queryState?.measureId,
    filters: queryState?.filters,
    where: buildWhereFromFilterValues(queryState?.filterValues),
  };

  // 1) Run the same pivot query (no paging; same API, same args)
  const { data } = await client.query<PivotQueryResult>({
    query: PIVOT_QUERY,
    variables,
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const pivot = data?.pivot;
  if (!pivot) {
    // Nothing to export; still open the modal to be consistent, or bail.
    await exportXlsModal({
      apolloClient: client,
      exportFileName: 'pivot_export',
      // Pass empty data so modal completes gracefully
      recordsData: [],
      columnsDef: [{ fieldName: 'rowLabel', fieldTitle: t('Γραμμή'), fieldType: 'TEXT' }],
    });
    return;
  }

  // 2) Build label maps from the returned trees
  // colKey -> human label (path "A / B / C")
  const colLabelMap = new Map<string, string>();
  const walkCols = (nodes: any[] | undefined, prefix = '') => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      const label = prefix ? `${prefix} / ${node.value}` : `${node.value}`;
      colLabelMap.set(node.key, label);
      if (node.children?.length) walkCols(node.children, label);
    }
  };
  walkCols(pivot.colTree);

  // rowKey -> human row label
  const rowLabelMap = new Map<string, string>();
  for (const leaf of pivot.rowLeaves ?? []) {
    const label = (leaf.path ?? []).map((p: any) => p?.value).join(' / ');
    rowLabelMap.set(leaf.key, label);
  }

  // 3) Build flat triples using colKey (safe prop name), not the human label
  const flat: Array<{ rowLabel: string; colKey: string; value: number }> = [];
  for (const cell of pivot.cells ?? []) {
    flat.push({
      rowLabel: rowLabelMap.get(cell.rowKey) ?? '',
      colKey: cell.colKey, // <-- safe, stable property
      value: cell.value,
    });
  }

  // 4) Pivot to wide records and collect ordered colKeys
  const { data: wideRows, colKeys } = pivotToWide(flat);

  // 5) Build columns using colKey for fieldName and human label for fieldTitle
  const colsMeta = colKeys.map((key) => ({ key, title: colLabelMap.get(key) ?? key }));
  const columnsDef = buildColumns(t, colsMeta);

  // 6) Hand the modal a static columnsDef + ready recordsData
  //    -> it will NOT fetch via hooks, so no invalid hook calls, and headers match data keys.
  await exportXlsModal({
    apolloClient: client,
    exportFileName: 'pivot_export',
    recordsData: wideRows, // modal uses this branch immediately
    columnsDef, // static array, matches data keys
    // (filterVariables/getLazyQuery not needed when recordsData is provided)
  });
}
